import {invalidateOnSuccess, omneticApi} from '@dms/api/base';
import type {TaskResponseBody} from '@dms/api/core';

import {
  CreateDirectSaleActivityRequestBody,
  DirectSaleActivityResponseBody,
  DirectSaleActivityResponseBodySchema,
  PatchDirectSaleActivityRequestBody,
} from './types';

const directSaleActivityTags = (directSaleId: string) => [
  {type: 'DirectSaleActivity' as const, id: directSaleId},
  {type: 'DirectSaleUpcomingTasks' as const, id: directSaleId},
];

export const directSaleActivityApi = omneticApi.injectEndpoints({
  endpoints: (build) => ({
    getDirectSaleActivity: build.query<DirectSaleActivityResponseBody[], {directSaleId: string}>({
      query: ({directSaleId}) => ({
        url: `/dms/v1/warehouse/direct-sale/${directSaleId}/activity`,
      }),
      providesTags: (_result, _error, {directSaleId}) => [
        {type: 'DirectSaleActivity', id: directSaleId},
      ],
      extraOptions: {responseSchema: DirectSaleActivityResponseBodySchema.array()},
    }),
    getDirectSaleUpcomingTasks: build.query<TaskResponseBody[], {directSaleId: string}>({
      query: ({directSaleId}) => ({
        url: `/dms/v1/warehouse/direct-sale/${directSaleId}/upcoming-tasks`,
      }),
      providesTags: (_result, _error, {directSaleId}) => [
        {type: 'DirectSaleUpcomingTasks', id: directSaleId},
      ],
    }),
    createDirectSaleActivity: build.mutation<
      void,
      {directSaleId: string; body: CreateDirectSaleActivityRequestBody}
    >({
      query: ({directSaleId, body}) => ({
        url: `/dms/v1/warehouse/direct-sale/${directSaleId}/activity`,
        method: 'POST',
        body,
      }),
      invalidatesTags: invalidateOnSuccess((_result, {directSaleId}) =>
        directSaleActivityTags(directSaleId)
      ),
    }),
    patchDirectSaleActivity: build.mutation<
      void,
      {directSaleId: string; activityId: string; body: PatchDirectSaleActivityRequestBody}
    >({
      query: ({directSaleId, activityId, body}) => ({
        url: `/dms/v1/warehouse/direct-sale/${directSaleId}/activity/${activityId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: invalidateOnSuccess((_result, {directSaleId}) =>
        directSaleActivityTags(directSaleId)
      ),
    }),
    deleteDirectSaleActivity: build.mutation<void, {directSaleId: string; activityId: string}>({
      query: ({directSaleId, activityId}) => ({
        url: `/dms/v1/warehouse/direct-sale/${directSaleId}/activity/${activityId}`,
        method: 'DELETE',
      }),
      invalidatesTags: invalidateOnSuccess((_result, {directSaleId}) =>
        directSaleActivityTags(directSaleId)
      ),
    }),
  }),
});

export const {
  useGetDirectSaleActivityQuery,
  useGetDirectSaleUpcomingTasksQuery,
  useCreateDirectSaleActivityMutation,
  usePatchDirectSaleActivityMutation,
  useDeleteDirectSaleActivityMutation,
} = directSaleActivityApi;
